import type { PostHog } from "posthog-js";
import { Capacitor } from "@capacitor/core";
import { App as NativeApp } from "@capacitor/app";

type Properties = Record<string, string | number | boolean | null | undefined>;
const allowed = new Set(`count native step next_step editing submit role application_status commitment_level has_lift_goal has_learn_goal has_lift_stretch has_learn_stretch enabled action activity_type has_media media_count has_text goal_tier source tab topic_type entry_type attachment_count result_count request_type decision has_feedback completed week_index changed_count token_available category automatic validation media_type surface screen operation error_code error_kind http_status online web_release native_version native_build platform environment`.split(" "));
// PostHog is about a third of the startup code, so it loads once the app is idle; calls made
// before then wait in a short queue and replay in order.
let ready = false;
let posthog: PostHog | undefined;
const queued: ((sdk: PostHog) => void)[] = [];
function withSdk(call: (sdk: PostHog) => void): void {
  if (!ready) return;
  if (!posthog) { if (queued.length < 100) queued.push(call); return; }
  try { call(posthog); } catch { /* Best effort. */ }
}
const whenIdle = (run: () => void) => typeof requestIdleCallback === "function"
  ? requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1000);
let identity: string | null = null;
let identityInitialized = false;
let screen = "startup";
const seen = new WeakSet<object>();
const lastErrors = new Map<string, number>();
const context: Properties = { platform: Capacitor.getPlatform(), environment: typeof location !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(location.hostname) ? "qa" : import.meta.env.MODE, web_release: import.meta.env.VITE_WEB_RELEASE || "development" };

export function safeProperties(properties: Properties = {}): Properties {
  return Object.fromEntries(Object.entries(properties).filter(([key, value]) => allowed.has(key) &&
    (value === null || typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value)) ||
      (typeof value === "string" && /^[a-zA-Z0-9_.:/-]{1,100}$/.test(value)))));
}
export function screenName(path: string): string {
  const first = path.split(/[/?#]/).filter(Boolean)[0];
  return ["post", "person", "home", "onboarding", "profile", "discuss", "manage", "settings", "notifications", "notification-preferences", "review-requests", "privacy"].includes(first) ? first : "home";
}
// Never send original messages, causes, request bodies or signed URLs. Keep only
// compiled application frame locations so uploaded source maps remain useful.
export function safeException(error: unknown, operation: string): Error {
  const kind = error instanceof Error && /^(Error|TypeError|RangeError|ReferenceError|SyntaxError|URIError)$/.test(error.name) ? error.name : "Error";
  const result = new Error(`LiftLearn:${operation.replace(/[^a-zA-Z0-9_.-]/g, "_").slice(0,80)}`);
  result.name = kind;
  const frames = error instanceof Error ? [...(error.stack || "").matchAll(/(?:https:\/\/[^\s/]+)?(\/assets\/[A-Za-z0-9_.-]+\.js):(\d+):(\d+)/g)].slice(0,12) : [];
  result.stack = `${kind}: ${result.message}\n` + frames.map(m => `    at https://lift-and-learn-web.pages.dev${m[1]}:${m[2]}:${m[3]}`).join("\n");
  return result;
}
export function initTelemetry(): Promise<void> {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key || (import.meta.env.DEV && import.meta.env.VITE_POSTHOG_DEV !== "true")) return Promise.resolve();
  ready = true;
  posthog = undefined;
  return new Promise<void>(resolve => whenIdle(() => void import("posthog-js").then(({ default: sdk }) => start(sdk, key))
    .catch(() => { ready = false; queued.length = 0; }).finally(resolve)));
}
function start(sdk: PostHog, key: string): void {
  try {
    sdk.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
      autocapture: false, capture_pageview: false, capture_pageleave: false,
      capture_exceptions: false, capture_dead_clicks: false,
      capture_heatmaps: false, capture_performance: false, ip: false,
      disable_session_recording: true, disable_surveys: true,
      enable_recording_console_log: false, person_profiles: "identified_only",
      advanced_disable_feature_flags: true, advanced_disable_flags: true,
      disable_external_dependency_loading: true,
      before_send: event => {
        if (!event) return null;
        delete event.$set;
        delete event.$set_once;
        // Drop SDK URL/referrer/person enrichment; explicit properties are the contract.
        const safe = safeProperties(event.properties as Properties);
        const system = ["token", "distinct_id", "$device_id", "$user_id", "$session_id", "$window_id", "$lib", "$lib_version", "$browser", "$browser_version", "$os", "$os_version", "$device_type", "$is_identified", "$anon_distinct_id", "$exception_list", "$exception_fingerprint", "$exception_level", "$release_id"];
        for (const k of system) if (event.properties[k] !== undefined) safe[k] = event.properties[k];
        safe.$geoip_disable = true;
        event.properties = safe;
        return event;
      },
    });
    posthog = sdk;
    for (const call of queued.splice(0)) { try { call(sdk); } catch { /* Best effort. */ } }
    if (Capacitor.isNativePlatform()) void NativeApp.getInfo().then(info => {
      context.native_version = info.version; context.native_build = info.build;
    }).catch(() => {});
  } catch { ready = false; queued.length = 0; /* Monitoring must never break app startup. */ }
}
export function identifyTelemetry(userId: string | null): void {
  if (!ready || (identityInitialized && userId === identity)) return;
  if (identity || (!identityInitialized && userId === null)) withSdk(sdk => sdk.reset());
  identityInitialized = true;
  identity = userId;
  if (userId) withSdk(sdk => sdk.identify(userId));
}
export function track(event: string, properties: Properties = {}): void {
  if (!ready || !/^[a-z][a-z0-9_]{0,79}$/.test(event)) return;
  const payload = { ...context, screen, ...safeProperties(properties) };
  withSdk(sdk => sdk.capture(event, payload));
}
export function trackScreen(path: string): void {
  const next = screenName(path);
  if (screen === next) return;
  screen = next; track("screen_viewed");
}
export function errorMetadata(error: unknown): Properties {
  const value = error && typeof error === "object" ? error as Record<string, unknown> : {};
  const code = typeof value.code === "string" ? value.code : undefined;
  const status = value.status;
  return {
    ...(code && /^(?:[0-9A-Z]{3,12}|auth\/[a-z-]{1,50})$/.test(code) ? { error_code: code } : {}),
    ...(typeof status === "number" && Number.isInteger(status) && status >= 400 && status <= 599 ? { http_status: status } : {}),
    ...(typeof navigator !== "undefined" ? { online: navigator.onLine } : {}),
  };
}
export function reportError(error: unknown, operation: string, properties: Properties = {}): void {
  if (!ready || (error instanceof Error && error.name === "AbortError")) return;
  if (error && typeof error === "object") {
    if (seen.has(error)) return;
    seen.add(error);
  }
  const clean = safeException(error, operation);
  const metadata = errorMetadata(error);
  const key = `${operation}:${properties.source || ""}:${metadata.error_code || ""}:${metadata.http_status || ""}:${clean.stack}`;
  const now = Date.now();
  if (now - (lastErrors.get(key) || 0) < 30_000) return;
  if (lastErrors.size >= 100) lastErrors.clear();
  lastErrors.set(key, now);
  const details = { ...context, screen, ...safeProperties(properties), operation: clean.message.slice(10), error_kind: clean.name, ...metadata };
  withSdk(sdk => sdk.captureException(clean, details));
}
export function watchWebErrors(): () => void {
  const onError = (e: ErrorEvent) => reportError(e.error, "javascript");
  const onRejection = (e: PromiseRejectionEvent) => reportError(e.reason, "unhandled_promise");
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => { window.removeEventListener("error", onError); window.removeEventListener("unhandledrejection", onRejection); };
}
